import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { hasRequiredVertical, localStorageSignal, NAVIGATION_TABS } from '../../helpers';
import { LoggedInCallback } from '../../service/cognito.service';
import { DebugService } from '../../service/debug.service';
import { MediaService } from '../../service/media.service';
import { PosthogService } from '../../service/posthog.service';
import { UserLoginService } from '../../service/user-login.service';

@Component({
  templateUrl: './secure-home.component.html',
  styleUrls: ['./secure-home.component.scss'],
  standalone: false,
})
export class SecureHomeComponent implements OnInit, LoggedInCallback {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private posthogService = inject(PosthogService);
  public userService = inject(UserLoginService);
  public debug = inject(DebugService);

  public mediaService = inject(MediaService);

  private readonly allTabs = NAVIGATION_TABS;
  private readonly routesToNotRerouteFor = ['request-access', 'location', 'installer-dashboard/', 'user-preferences'];

  public hasLoaded = signal<boolean>(false);
  public userFullName = signal<string>('user');

  public visibleTabs = computed(() => {
    const isReady = this.posthogService.isReady();
    if (!isReady) return [];

    if (!this.userService.currentUserAcceptedTOS()) return [];

    const roles = this.userService.currentProfileRoleList();
    const tabFeatureFlags = Object.fromEntries(
      this.allTabs.map((t) => {
        return [t.path, !!this.posthogService.isFeatureEnabled('tab_' + t.path, true)];
      }),
    );
    const profile = this.userService.currentProfile();
    const isNotActive = profile?.account_status !== 'Active';
    const isAccessSuspended = profile?.access_suspended ?? false;
    const userVerticals = profile?.business_verticals ?? [];
    const isAdmin = this.userService.currentUserIsAdmin();

    return this.allTabs.filter((menuTab) => {
      let enabled = true;
      if (menuTab.featureFlagName && this.userService.currentProfile()) {
        enabled = this.posthogService.evaluateRouteFeatureFlag(menuTab.featureFlagName, this.userService.currentProfile());
      }

      const hasAllowedInactiveRole =
        !menuTab.allowedIfInactiveRoles?.length || menuTab.allowedIfInactiveRoles.some((role) => roles.includes(role));
      const hasAllowedSuspendedRole =
        !menuTab.allowedIfSuspendedRoles?.length || menuTab.allowedIfSuspendedRoles.some((role) => roles.includes(role));
      const allowedWhenInactive = menuTab.allowedIfInactive && hasAllowedInactiveRole;
      const allowedForAccountStatus = isAccessSuspended || !isNotActive || allowedWhenInactive;
      const allowed = menuTab.allowed.some((r) => roles.includes(r)) && tabFeatureFlags[menuTab.path] && allowedForAccountStatus;
      return (
        enabled &&
        allowed &&
        (!isAccessSuspended || (menuTab.allowedIfSuspended && hasAllowedSuspendedRole)) &&
        hasRequiredVertical(menuTab.verticals, userVerticals, isAdmin)
      );
    });
  });

  /** Display friendly message to user explaining that their user setup is incomplete or misconfigured. */
  public isIncompleteUser = computed(() => this.visibleTabs().length === 0);

  public activeTab = { prefix: undefined, path: undefined };

  public collapsedSidenavToIcons = localStorageSignal<boolean>(false, 'collapse-sidenav-to-icons');

  constructor() {
    this.userService.isAuthenticated(this);
  }

  ngOnInit() {
    combineLatest([this.userService.auth.user$, this.userService.currentProfileLoaded$]).subscribe(([claims, profile]) => {
      if (!claims || !profile) return;
      const nameSplit = claims.name.split(' ');
      const givenName = claims.given_name ?? nameSplit.shift();
      const familyName = claims.family_name ?? nameSplit.join(' ');
      this.userFullName.set(`${givenName} ${familyName}`);
      this.refreshPermissions();
    });
  }

  refreshPermissions() {
    this.userService.getMe().subscribe((profile) => {
      if (profile === null || profile.user_id === 'anonymous') {
        this.userService.navigateToLogin(this.router.url || '/home');
        return;
      }

      const visibleTabs = this.visibleTabs();
      if (visibleTabs.length > 0 && !this.routesToNotRerouteFor.some((t) => this.router.url.includes(t))) {
        const activeTab = visibleTabs.find((tab) => this.router.url.replace('/home/', '').startsWith(tab.path));
        this.setActiveTab(activeTab || visibleTabs[0]);
        this.router.navigate([], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
        });
      }

      this.hasLoaded.set(true);
    });
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.userService.navigateToLogin(this.router.url || '/home');
    }
  }

  setActiveTab(tab) {
    if (tab.target === '_blank') return;
    this.activeTab = tab;
  }
}
